"use client";

import React, { useMemo, useState } from "react";
import { LayoutGrid, Lightbulb } from "lucide-react";
import { DynamicIcon, iconNames, type IconName } from "lucide-react/dynamic";
import { cn } from "~/lib/utils";
import GlowingButton from "~/components/reusables/glowing-button";
import { analytics } from "lib/segment";

export interface Presenter {
  id?: string | number;
  name: string;
  role?: string;
}

export interface Session {
  id: string | number;
  title: string;
  subtitle?: string;
  presenters: Presenter[];
  start: string;
  end: string;
}

export interface Track {
  id: string | number;
  key: string;
  label: string;
  icon?: string;
  accentColor: string;
  tagline?: string;
  description?: string;
  avatarUrl?: string;
  sessions: Session[];
}

type NormalizedPresenter = Presenter & { id: string };
type NormalizedSession = Omit<Session, "id" | "presenters"> & {
  id: string;
  presenters: NormalizedPresenter[];
};
type NormalizedTrack = Omit<Track, "id" | "sessions"> & {
  id: string;
  sessions: NormalizedSession[];
};

const hasId = (value: string | number | undefined) =>
  value !== undefined && String(value) !== "";

// Parts are URL-encoded so the ":" separator can never appear inside a part.
const contentId = (prefix: string, ...parts: Array<string | number | undefined>) =>
  [prefix, ...parts].map((part) => encodeURIComponent(String(part ?? "").trim())).join(":");

// Strapi component entries are expected to carry unique IDs; content-based IDs
// are only a fallback. Duplicates are suffixed so React keys stay unique, and
// logged as errors in development because they mean the input data is wrong.
// Not thrown: a render error would blank the whole landing page.
const createIdAllocator = (list: string) => {
  const issued = new Set<string>();
  return (base: string) => {
    if (issued.has(base) && process.env.NODE_ENV !== "production") {
      console.error(`TrackAgenda: duplicate ${list} ID "${base}"; suffixing to keep keys unique.`);
    }
    let id = base;
    for (let n = 1; issued.has(id); n++) id = `${base}#${n}`;
    issued.add(id);
    return id;
  };
};

const withStableIds = (tracks: Track[]): NormalizedTrack[] => {
  const allocateTrackId = createIdAllocator("track");
  return tracks.map((track) => {
    const trackId = allocateTrackId(
      hasId(track.id) ? String(track.id) : contentId("track", track.key),
    );
    const allocateSessionId = createIdAllocator("session");
    return {
      ...track,
      id: trackId,
      sessions: track.sessions.map((session) => {
        const sessionId = allocateSessionId(
          hasId(session.id)
            ? String(session.id)
            : contentId("session", trackId, session.title, session.start, session.end),
        );
        const allocatePresenterId = createIdAllocator("presenter");
        return {
          ...session,
          id: sessionId,
          presenters: session.presenters.map((presenter) => ({
            ...presenter,
            id: allocatePresenterId(
              hasId(presenter.id)
                ? String(presenter.id)
                : contentId("presenter", sessionId, presenter.name, presenter.role),
            ),
          })),
        };
      }),
    };
  });
};

interface TrackAgendaProps {
  title?: string;
  tracks: Track[];
  registerLabel?: string;
}

const VIEW_ALL_ICON = LayoutGrid;
const PILL_ICON_CLASS = "size-3.5 shrink-0 md:size-4";
const DEFAULT_TRACK_ICON = Lightbulb;

// Old subject names that were stored before editors typed Lucide names.
const LEGACY_ICON_NAMES: Record<string, string> = {
  ideate: "lightbulb",
  build: "wrench",
  land: "rocket",
  elevate: "star",
};

const LUCIDE_ICON_NAMES: ReadonlySet<string> = new Set(iconNames);

const toLucideName = (raw?: string): IconName | null => {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  const aliased = LEGACY_ICON_NAMES[trimmed.toLowerCase()] ?? trimmed;
  const kebab = /^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(aliased)
    ? aliased.toLowerCase()
    : aliased
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();

  return LUCIDE_ICON_NAMES.has(kebab) ? (kebab as IconName) : null;
};

const DynamicTrackIcon = ({
  name,
  className,
  size,
  style,
}: {
  name?: string;
  className?: string;
  size?: number;
  style?: React.CSSProperties;
}) => {
  const lucideName = toLucideName(name);
  if (!lucideName) {
    return <DEFAULT_TRACK_ICON className={className} size={size} style={style} />;
  }

  return (
    <DynamicIcon
      name={lucideName}
      fallback={() => (
        <DEFAULT_TRACK_ICON className={className} size={size} style={style} />
      )}
      className={className}
      size={size}
      style={style}
    />
  );
};

// Only #RRGGBB is accepted so accentRgba can parse every accent the same way.
const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

const FALLBACK_ACCENT_COLOR = "#94E147";

const ACCENT_COLOR_HEX: Record<string, string> = {
  green: "#94E147",
  amber: "#FFBD33",
  sky: "#40CDF0",
  purple: "#B376EA",
  // Leftover hex from earlier dropdown values.
  "#74bf4b": "#94E147",
  "#d97706": "#FFBD33",
  "#0ea5e9": "#40CDF0",
  "#9345e1": "#B376EA",
};

// accentColor is a named color enum in Strapi (Green / Amber / Sky / Purple).
// Older entries may still store hex. Either shape becomes a CSS color;
// anything else falls back to green.
const sanitizeAccentColor = (color?: string): string => {
  const trimmed = color?.trim() ?? "";
  const named = ACCENT_COLOR_HEX[trimmed.toLowerCase()];
  if (named) return named;
  if (HEX_COLOR_REGEX.test(trimmed)) return trimmed;
  return FALLBACK_ACCENT_COLOR;
};

const accentRgba = (color: string | undefined, opacity: number): string => {
  const hex = sanitizeAccentColor(color);
  const r = Number(`0x${hex.slice(1, 3)}`);
  const g = Number(`0x${hex.slice(3, 5)}`);
  const b = Number(`0x${hex.slice(5, 7)}`);
  const alpha = Math.min(Math.max(opacity, 0), 1);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const TrackAgenda = ({
  title,
  tracks,
  registerLabel = "Register now",
}: TrackAgendaProps) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const normalizedTracks = useMemo(() => withStableIds(tracks ?? []), [tracks]);

  const toggleTrack = (key: string) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const resetToAll = () => setSelectedKeys([]);

  const showFilters = normalizedTracks.length >= 2;

  const visibleTracks = useMemo(() => {
    if (!showFilters || selectedKeys.length === 0) return normalizedTracks;
    return normalizedTracks.filter((track) => selectedKeys.includes(track.key));
  }, [normalizedTracks, selectedKeys, showFilters]);

  if (normalizedTracks.length === 0) {
    return null;
  }

  return (
    <section
      id="track-agenda"
      className="w-full scroll-mt-24 bg-black px-2 font-light text-white lg:px-14 xl:px-16"
      aria-labelledby="track-agenda-heading"
    >
      <div className="container mx-auto px-2 py-16 lg:px-0 lg:py-24">
      <div className="flex flex-col gap-6 pb-10 sm:flex-row sm:items-center sm:justify-between">
        {title && (
          <h2
            id="track-agenda-heading"
            className="text-5xl font-light leading-120 text-white"
          >
            {title}
          </h2>
        )}
        <RegisterNowButton label={registerLabel} className="shrink-0 self-start sm:self-center" />
      </div>

      {showFilters && (
        <div
          role="group"
          aria-label="Filter sessions by track"
          className="mb-10 flex flex-wrap items-center gap-3"
        >
          <FilterPill
            label="View all tracks"
            icon={<VIEW_ALL_ICON className={PILL_ICON_CLASS} />}
            active={selectedKeys.length === 0}
            onClick={resetToAll}
          />
          {normalizedTracks.map((track) => (
            <FilterPill
              key={track.id}
              label={track.label}
              icon={<DynamicTrackIcon name={track.icon} className={PILL_ICON_CLASS} />}
              active={selectedKeys.includes(track.key)}
              accentColor={sanitizeAccentColor(track.accentColor)}
              onClick={() => toggleTrack(track.key)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col gap-10">
        {visibleTracks.map((track) => (
          <TrackBlock key={track.id} track={track} />
        ))}
      </div>
      <div className="flex justify-center pt-12">
        <RegisterNowButton label={registerLabel} />
      </div>
      </div>
    </section>
  );
};

const RegisterNowButton = ({
  label,
  className,
}: {
  label: string;
  className?: string;
}) => (
  <GlowingButton className={className}>
    <a
      href="#hubspot-form"
      className="flex items-center text-base"
      onClick={() => {
        if (
          typeof window !== "undefined" &&
          (window.OnetrustActiveGroups == undefined ||
            window.OnetrustActiveGroups?.includes("2"))
        ) {
          analytics.track("Event Register clicked", {
            link_label: { register: label },
            location: "Body - Track Agenda section",
            resource_interaction: "",
          });
        }
      }}
    >
      {label}
    </a>
  </GlowingButton>
);

interface FilterPillProps {
  label: string;
  /** Pre-rendered icon element, e.g. `<LayoutGrid className={PILL_ICON_CLASS} />`, not a component type. */
  icon: React.ReactNode;
  active: boolean;
  accentColor?: string;
  onClick: () => void;
}

const FilterPill = ({ label, icon, active, accentColor, onClick }: FilterPillProps) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const highlighted = hovered || focused;
  const accentStyle: React.CSSProperties | undefined = accentColor
    ? {
        backgroundColor: accentRgba(accentColor, active ? 0.28 : highlighted ? 0.16 : 0.08),
        borderColor: accentRgba(accentColor, active ? 0.6 : highlighted ? 0.4 : 0.2),
        color: accentRgba(accentColor, active || highlighted ? 1 : 0.8),
      }
    : undefined;

  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={accentStyle}
      className={cn(
        "flex h-10 items-center gap-2 rounded-full border px-3 text-13 font-bold leading-normal transition-colors md:h-12 md:px-4 md:text-15",
        !accentColor &&
          (active
            ? "border-white/60 bg-white/35 text-white"
            : "border-white/20 bg-white/12 text-white hover:border-white/40 hover:bg-white/22 focus-visible:border-white/40 focus-visible:bg-white/22"),
      )}
    >
      {icon}
      {label}
    </button>
  );
};

const TrackBlock = ({ track }: { track: NormalizedTrack }) => {
  const sessionCount = track.sessions?.length ?? 0;
  const accentColor = sanitizeAccentColor(track.accentColor);

  return (
    <article>
      <header
        className="flex flex-col gap-4 rounded-md border-l-4 p-6 sm:flex-row sm:items-center"
        style={{
          borderColor: accentColor,
          backgroundColor: accentRgba(accentColor, 0.1),
        }}
      >
        <div className="flex shrink-0 items-center gap-4">
          <div>
            <h3
              className="text-xl font-normal leading-normal md:text-2xl md:leading-normal lg:text-3xl"
              style={{ color: accentColor }}
            >
              {track.label}
            </h3>
            {track.tagline && (
              <p className="font-p text-white">{track.tagline}</p>
            )}
            <p className="text-13 font-light leading-normal text-white">
              {sessionCount} session{sessionCount === 1 ? "" : "s"}
            </p>
          </div>
          {track.avatarUrl && (
            <img
              src={track.avatarUrl}
              alt=""
              className="h-10 w-10 rounded-full border-2"
              style={{ borderColor: accentColor }}
            />
          )}
        </div>
        {track.description && (
          <p className="grow shrink-0 basis-0 text-15 font-light leading-160 text-white">
            {track.description}
          </p>
        )}
      </header>

      <div role="table" aria-label={`${track.label} sessions`}>
        <div
          role="row"
          className="hidden border-b border-white/10 px-4 pb-2.5 pt-5 md:grid md:grid-cols-track-session md:items-center md:gap-4"
        >
          <p role="columnheader" className="text-xs font-bold leading-normal" style={{ color: accentColor }}>
            Session Title
          </p>
          <p role="columnheader" className="text-xs font-bold leading-normal" style={{ color: accentColor }}>
            Presenters
          </p>
          <p role="columnheader" className="text-xs font-bold leading-normal" style={{ color: accentColor }}>
            Start
          </p>
          <p role="columnheader" className="text-xs font-bold leading-normal" style={{ color: accentColor }}>
            End
          </p>
        </div>
        <div role="rowgroup" className="flex flex-col gap-4 md:gap-6">
          {track.sessions.map((session, index) => (
            <div
              key={session.id}
              role="row"
              className={cn(
                "relative grid grid-cols-1 gap-3 py-5 pl-5 pr-4 md:grid-cols-track-session md:items-start md:gap-4 md:pl-4",
                index % 2 === 0 && "bg-white/3",
              )}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute bottom-4 left-0 top-4 w-1"
                style={{ backgroundColor: accentColor }}
              />
              <div role="cell">
                <p className="text-13 font-normal leading-170 text-white/90">{session.title}</p>
                {session.subtitle && (
                  <p className="text-11 font-light leading-170" style={{ color: accentColor }}>
                    {session.subtitle}
                  </p>
                )}
              </div>
              <div role="cell">
                {session.presenters.map((presenter) => (
                  <div key={presenter.id} className="mb-1 last:mb-0">
                    <p className="text-13 font-normal leading-160 text-white/90">{presenter.name}</p>
                    {presenter.role && (
                      <p className="text-11 font-light leading-160 text-white/60">{presenter.role}</p>
                    )}
                  </div>
                ))}
              </div>
              <p role="cell" className="text-13 font-normal leading-160 text-white/90 md:hidden">
                {[session.start, session.end].filter(Boolean).join(" – ")}
              </p>
              <p role="cell" className="hidden text-13 font-normal leading-160 text-white/90 md:block">{session.start}</p>
              <p role="cell" className="hidden text-13 font-normal leading-160 text-white/90 md:block">{session.end}</p>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
};

export default TrackAgenda;
